import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useI18n } from './I18nContext';

type DialogType = 'alert' | 'confirm';

interface DialogState {
  type: DialogType;
  message: string;
  title?: string;
  resolve: (value: boolean) => void;
}

interface DialogContextValue {
  alert: (message: string, title?: string) => Promise<void>;
  confirm: (message: string, title?: string) => Promise<boolean>;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export function DialogProvider({ children }: { children: ReactNode }) {
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const alert = useCallback((message: string, title?: string): Promise<void> => {
    return new Promise((resolve) => {
      setDialog({ type: 'alert', message, title, resolve: () => resolve() });
    });
  }, []);

  const confirm = useCallback((message: string, title?: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialog({ type: 'confirm', message, title, resolve });
    });
  }, []);

  const handleClose = useCallback((result: boolean) => {
    if (dialog) {
      dialog.resolve(result);
      setDialog(null);
    }
  }, [dialog]);

  return (
    <DialogContext.Provider value={{ alert, confirm }}>
      {children}
      {dialog && (
        <DialogOverlay
          type={dialog.type}
          message={dialog.message}
          title={dialog.title}
          onClose={handleClose}
        />
      )}
    </DialogContext.Provider>
  );
}

export function useDialog(): DialogContextValue {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
}

// --- DialogOverlay ---

interface DialogOverlayProps {
  type: DialogType;
  message: string;
  title?: string;
  onClose: (result: boolean) => void;
}

function DialogOverlay({ type, message, title, onClose }: DialogOverlayProps) {
  const { t } = useI18n();
  // ESC로 닫기
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose(false);
      if (e.key === 'Enter') onClose(type === 'alert' ? true : true);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, type]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(2px)',
      }}
      onClick={() => onClose(false)}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '24px',
          minWidth: '320px',
          maxWidth: '440px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          animation: 'dialogIn 0.15s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div style={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '8px',
          }}>
            {title}
          </div>
        )}
        <div style={{
          fontSize: '14px',
          color: '#374151',
          lineHeight: '1.5',
          marginBottom: '20px',
          whiteSpace: 'pre-wrap',
        }}>
          {message}
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
        }}>
          {type === 'confirm' && (
            <button
              onClick={() => onClose(false)}
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: '#374151',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              {t.common.cancel}
            </button>
          )}
          <button
            onClick={() => onClose(true)}
            autoFocus
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: '#3b82f6',
              color: '#fff',
              fontSize: '14px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            {t.common.confirm}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes dialogIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

export default DialogContext;
